import { css } from '@emotion/react';
import {
  type FC, useCallback,
  useState,
} from 'react';
import { type GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import { MapMarkersSelectOverlayContainer } from '~/_shared/components/mapMarkersSelect/mapMarkersSelectOverlay.container';
import { CONFIG } from '~/_shared/constants/config';
import { BoundaryAdvancedEditConfirmContainer } from '~/boundary/editMode/drawConfirm/boundaryAdvancedEditConfirm.container';
import { DrawingToolContextMenuContainer } from '~/drawingTool/contextMenu/drawingToolContextMenu.container';
import { MapContextMenuContainer } from '~/map/contextMenu/mapContextMenu.container';
import { MapMarkerContextMenuContainer } from '~/map/contextMenu/mapMarkerContextMenu.container';
import { BoundaryDragEditContainer } from '~/map/map/boundary/boundaryDragEdit/boundaryDragEdit.container';
import { BoundaryDrawContainer } from '~/map/map/boundary/boundaryDraw/boundaryDraw.container';
import { BoundaryLassoSelectContainer } from '~/map/map/boundary/boundaryLassoSelect/boundaryLassoSelect.container';
import { DirectionsOverlayContainer } from '~/map/map/directions/directionsOverlay.container';
import { DistanceCalculatorOverlay } from '~/map/map/distanceCalculator/distanceCalculatorOverlay.container';
import { DrawingToolOverlay } from '~/map/map/drawingTool/drawingToolOverlay.container';
import { HeatmapsOverlay } from '~/map/map/heatmaps/heatmapsOverlay.container';
import { LassoToolOverlay } from '~/map/map/lassoTool/overlay/lassoToolOverlay.container';
import { MapLoaderContainer } from '~/map/map/loader/mapLoader.container';
import { MapContextProvider } from '~/map/map/mapContext';
import { MapToolsOverlay } from '~/map/map/mapTools/mapToolsOverlay.container';
import { MarkersLassoSelectContainer } from '~/map/map/markers/markersSelect/markersLassoSelect.container';
import { PlacesOverlay } from '~/map/map/places/placesOverlay';
import { ProximityOverlay } from '~/map/map/proximity/proximityOverlay.container';
import { SearchOverlayContainer } from '~/map/map/search/searchOverlay.container';
import { ShapeDrawOverlay } from '~/map/map/shapeDraw/shapeDrawOverlay.container';
import { useAddMapAttributionForPresentationalMap } from '~/map/map/useAddMapAttributionForPresentationalMap';
import { useWebGL } from '~/map/map/webgl/useWebGL';
import { useExposeMapTestingFunctions } from '~/testingMode/testingModeFunctions';
import { GoogleMapComponent } from '../../_shared/components/google-map/google-map.component';
import { BoundaryPolygonDragEditConfirmContainer } from '../../boundary/editMode/drawConfirm/boundaryPolygonEditConfirm.container';
import { BoundarySelectConfirmContainer } from '../../boundary/editMode/selectConfirm/boundarySelectConfirm.container';
import { DrawingToolConfirmContainer } from '../../drawingTool/confirm/drawingToolConfirm.container';
import { testingModeEnabled } from '../../testingMode/testingMode';
import { useMapBoundary } from './boundary/useMapBoundary';
import { useMarkerHoverEffects } from './mapOverlays/markerHoverEffects/useMarkerHoverEffects.hook';
import { useMarkers } from './markers/useMarkers.hook';
import { MoveMarkerLabelsSaveContainer } from './markers/useMoveMarkerLabels/moveMarkerLabelsSave.container';
import { MoveMarkersSaveContainer } from './markers/useMoveMarkers/moveMarkersSave.container';
import { useDebugBounds } from './useDebugBounds';
import { useGraphicSettings } from './useGraphicSettings.hook';
import { useMapFeatures } from './useMapFeatures.hook';
import { useMapOptions } from './useMapOptions.hook';
import { useMapStyles } from './useStyleManager.hook';

const wrapperStyle = css({
  height: '100%',
  width: '100%',
  position: 'relative',
});

const MapFeaturesWithContext: FC = () => {
  useMapFeatures();
  useMapOptions();
  const numberOfRenderedMarkers = useMarkers();
  useMapBoundary();
  useMapStyles();
  useMarkerHoverEffects();
  useDebugBounds();
  useGraphicSettings(numberOfRenderedMarkers);

  const exposeMapTestingFunctions = useExposeMapTestingFunctions();

  if (testingModeEnabled() || CONFIG.MODE === 'development') {
    exposeMapTestingFunctions();
  }

  return null;
};

export const MapComponent: FC = () => {
  const [map, setMap] = useState<GoogleMapInstance>();
  const [webglOverlay, webglLayers, markerTemplateManager] = useWebGL(map);
  const { addMapAttribution } = useAddMapAttributionForPresentationalMap();

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMap(map);
    addMapAttribution();
  }, [addMapAttribution]);

  return (
    <div
      data-testid="mapView"
      css={wrapperStyle}
    >
      <GoogleMapComponent onMapInitialized={handleMapInit} />

      <MoveMarkersSaveContainer />
      <MoveMarkerLabelsSaveContainer />
      <BoundarySelectConfirmContainer />
      <MapMarkersSelectOverlayContainer />
      <DrawingToolConfirmContainer />
      <BoundaryPolygonDragEditConfirmContainer />
      <BoundaryAdvancedEditConfirmContainer />

      <MapContextProvider
        map={map?.instance}
        webglLayers={webglLayers}
        webglOverlay={webglOverlay}
        markerTemplateManager={markerTemplateManager}
      >
        <MapFeaturesWithContext />
        <DistanceCalculatorOverlay />
        <HeatmapsOverlay />
        <DrawingToolOverlay />
        <DrawingToolContextMenuContainer />
        <MapToolsOverlay />
        <ProximityOverlay />
        <PlacesOverlay />
        <SearchOverlayContainer />
        <LassoToolOverlay />
        <ShapeDrawOverlay />
        <BoundaryDrawContainer />
        <BoundaryLassoSelectContainer />
        <MarkersLassoSelectContainer />
        <BoundaryDragEditContainer />
        <MapContextMenuContainer />
        <MapMarkerContextMenuContainer />
        <DirectionsOverlayContainer />
        <MapLoaderContainer />
      </MapContextProvider>
    </div>
  );
};
